import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { api } from './client';

type Friend = {
  id: string; name: string; email: string;
  city?: string | null; timezone?: string | null; sectors?: string[] | null;
  avatarUrl?: string | null; locationSharing?: string | null;
  currentCity?: string | null; currentCountry?: string | null; currentAt?: number | null;
  role?: string | null;
  ddReviewerAt?: number | null;
};
type Ctx = {
  friend: Friend | null;
  needsOnboarding: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
  setFriendDirect: (f: Friend, needsOnboarding: boolean) => void;
  logout: () => void;
};
const AuthCtx = createContext<Ctx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [friend, setFriend] = useState<Friend | null>(null);
  const [needsOnboarding, setNeeds] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.me();
      setFriend(r.friend);
      setNeeds(!!r.needsOnboarding);
    } catch {
      setFriend(null); setNeeds(false);
    } finally {
      setLoading(false);
    }
  };

  // Set the auth state immediately after signup/signin, no extra fetch.
  const setFriendDirect = (f: Friend, no: boolean) => {
    setFriend(f);
    setNeeds(no);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  return <AuthCtx.Provider value={{
    friend, needsOnboarding, loading,
    refresh, setFriendDirect,
    logout: async () => {
      try { await api.logout(); } catch {}
      setFriend(null); setNeeds(false); setLoading(false);
    },
  }}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const c = useContext(AuthCtx);
  if (!c) throw new Error('Auth outside provider');
  return c;
}
